import { storeToRefs } from 'pinia'
import { MaybeRefOrGetter } from 'vue'

import { sharedSelect } from '#.shared/api/utils/sharedSelect'

import { useShiftQuery } from '../../shifts/composables/useShiftQuery'
import { useSiteContext } from '../../sites/composables/useSiteContext'

import { Pagination } from '../../common/models/pagination'
import { Shift } from '../../shifts/models/shift'

import { useScheduleFilterStore } from '../stores/scheduleFilterStore'

import {
  countUnpublished,
  publishableByArea,
  scheduleTotals,
} from '../domain/aggregate'
import { SCHEDULE_STALE_TIME } from './scheduleQueryOptions'

/*
|--------------------------------------------------------------------------
| Schedule shift queries
|--------------------------------------------------------------------------
|
| One query, keyed by the schedule's visible range and building, that every
| part of the schedule reads through a `select`. The selectors are shared
| (see sharedSelect): vue-query memoises a selector per observer, and every
| cell on the grid is an observer, so without sharing each fetch would be
| re-aggregated once per cell. With it, a fetch is aggregated once and the
| cells, the stats row and the publish button all read that one result.
*/

/** Page size for the range; every page is fetched, so this only sets how many requests it takes. */
const SHIFTS_PER_PAGE = 300

type ShiftPage = { shifts: Shift[]; pagination: Pagination }

export function useScheduleShiftQuery<T = ShiftPage>(options?: {
  select?: (data: ShiftPage) => T
  enabled?: MaybeRefOrGetter<boolean>
}) {
  const { dateFrom, dateTo, building, jobRole, showCancelledShifts } =
    storeToRefs(useScheduleFilterStore())
  const { site } = useSiteContext()

  return useShiftQuery(
    () => site.value.id,
    {
      dateFrom,
      dateTo,
      building,
      jobRole,
      status: () => (showCancelledShifts.value ? undefined : 'active'),
      perPage: () => SHIFTS_PER_PAGE,
      allPages: () => true,
    },
    { select: options?.select, staleTime: SCHEDULE_STALE_TIME, enabled: options?.enabled },
  )
}

const selectShifts = sharedSelect((data: ShiftPage) => data.shifts)
const selectTotals = sharedSelect((data: ShiftPage) =>
  scheduleTotals(data.shifts),
)
const selectPublishableByArea = sharedSelect((data: ShiftPage) =>
  publishableByArea(data.shifts),
)
const selectUnpublishedCount = sharedSelect((data: ShiftPage) =>
  countUnpublished(data.shifts),
)

export function useScheduleShifts() {
  return useScheduleShiftQuery({ select: selectShifts })
}

/** Worked minutes and cost by date, area-and-date and employee. */
export function useScheduleTotals() {
  return useScheduleShiftQuery({ select: selectTotals })
}

/** `enabled`: the Publish dialog is mounted on every org page, so it loads the list only while open */
export function useSchedulePublishableByArea(enabled?: MaybeRefOrGetter<boolean>) {
  return useScheduleShiftQuery({ select: selectPublishableByArea, enabled })
}

export function useScheduleUnpublishedCount() {
  return useScheduleShiftQuery({ select: selectUnpublishedCount })
}
